import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import centroid from '@turf/centroid'
import distance from '@turf/distance'
import envelope from '@turf/envelope'
import { point, polygon } from '@turf/helpers'
import endsWith from 'lodash/endsWith'
import get from 'lodash/get'
import isArray from 'lodash/isArray'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import remove from 'lodash/remove'
import toLower from 'lodash/toLower'
import trim from 'lodash/trim'
import uniqWith from 'lodash/uniqWith'

import * as PoziUtils from 'api/utils'
import globalTransformerCrownLandTenureCode from 'config/transformers/global_transformer_cltenure_code.json'
import globalTransformerCrownLandTenureType from 'config/transformers/global_transformer_cltenure_type.json'
import globalTransformerSoilType from 'config/transformers/global_transformer_soil_type.json'

import Garbage from './transformers/garbage'

/**
 * Transforms data structures. Calculates elements.
 * 1) Add a new transformer to transform and define if it runs by default, or not.
 * 2) Create a function that takes the data parameter, and then returns the same data parameter with updated data.
 */

// A (Adapter)-> PoziDataTransformer function is implemented in order to follow the standard Adapter pattern
function PoziDataTransformer () {
  // Adapters
  const adapters = {
    transform: function (cardProcessing, data, coords) {
      return transform(cardProcessing, data, coords)
    }
  }

  const app = PoziUtils.getApp()

  /**
   * Main entry point
   * @param {Object} cardProcessing Pozi config structure
   * @param {Object} data Either GeoJSON or flat json
   */
  var transform = function (cardProcessing, data, coordsIn) {
    if (!data) {
      console.log('PoziDataTransformer - transform - no data provided', cardProcessing, data, coordsIn)
      return data
    }

    let transforms = {}

    if (cardProcessing && cardProcessing.transformer) {
      transforms = cardProcessing.transformer
    }

    let coords = null
    if (isNil(coordsIn)) {
      coords = get(transforms, 'coords', null)
    } else {
      coords = coordsIn
    }

    let flatData

    // Handle flat structure.
    if (data.properties) {
      flatData = data.properties
      data.properties = runFlatDataTransforms(cardProcessing, transforms, flatData)
    } else if (data[0] && data[0].properties) {
      for (const key in data) {
        if (data[key].properties) {
          flatData = data[key].properties
          data[key].properties = runFlatDataTransforms(cardProcessing, transforms, flatData)
        }
      }
    } else if (data && data.features) {
      // These transforms only work on the GeoJSON
      if (coords) {
        data = includeTransform(transforms.sortByClosestToSelectionMarker, false) ? sortByClosestToSelectionMarker(data, coords) : data
        data = includeTransform(transforms.nearestFive, false) ? nearestFive(data, coords) : data
        data = includeTransform(transforms.pointOnSurface, false) ? pointOnSurface(data, coords) : data
      }
      data = includeTransform(transforms.geomaskEnvelope, false) ? geomaskEnvelope(cardProcessing, data) : data
      data = includeTransform(transforms.geomask, false) ? geomask(cardProcessing, data) : data
      data = includeTransform(transforms.filter, false) ? filter(cardProcessing, data) : data

      // !! ALWAYS RUNS !!

      data = includeTransform(transforms.removeDuplicates, true) ? removeDuplicates(data) : data
    } else {
      flatData = data
      data = runFlatDataTransforms(cardProcessing, transforms, flatData)
    }

    return data
  }

  // ============================================================
  // Private Functions
  // ============================================================

  var runFlatDataTransforms = function (cardProcessing, transforms, flatData) {
    flatData = includeTransform(transforms.removeZOnDates, true) ? removeZFromEndOfDates(flatData) : flatData
    flatData = includeTransform(transforms.garbage, false) ? Garbage(flatData) : flatData
    // flatData = includeTransform(transforms.deleteblanks, true) ? removeEmptyFields(flatData) : flatData
    flatData = includeTransform(transforms.prettyDateNoTime, false) ? formatDates(flatData, false) : flatData
    flatData = includeTransform(transforms.prettyDateWithTime, false) ? formatDates(flatData, false) : flatData
    flatData = includeTransform(transforms.vmCLTenure, false) ? vmCLTenureLookup(flatData, false) : flatData
    flatData = includeTransform(transforms.soilType, false) ? soilTypeLookup(flatData, false) : flatData
    flatData = includeTransform(transforms.prependIDFieldNameToTitle, false) ? prependIDFieldNameToTitle(cardProcessing, flatData) : flatData
    flatData = includeTransform(transforms.postpendToTitle, false) ? postpendToTitle(cardProcessing, flatData, transforms.postpendToTitle) : flatData
    flatData = includeTransform(transforms.calculatedField, false) ? calculatedField(flatData, transforms.calculatedField) : flatData
    flatData = includeTransform(transforms.orderFields, false) ? orderFields(flatData, transforms.orderFields) : flatData
    flatData = includeTransform(transforms.renameAndOrderFields, false) ? renameAndOrderFields(flatData, transforms.renameAndOrderFields) : flatData
    flatData = includeTransform(transforms.addFeatureFields, false) ? addFeatureFields(flatData, transforms.addFeatureFields) : flatData
    flatData = includeTransform(transforms.alterfield, false) ? alterfield(flatData, transforms.alterfield) : flatData
    flatData = includeTransform(transforms.roundValue, false) ? roundValue(flatData, transforms.roundValue) : flatData
    flatData = includeTransform(transforms.hidePastEvents, false) ? hidePastEvents(flatData, transforms.hidePastEvents) : flatData
    flatData = includeTransform(transforms.removeFields, false) ? removeFields(flatData, transforms.removeFields) : flatData
    flatData = includeTransform(transforms.removeFieldsArr, false) ? removeFieldsArr(flatData, transforms.removeFieldsArr) : flatData
    flatData = includeTransform(transforms.sliceFields, false) ? sliceFields(flatData, transforms.sliceFields) : flatData

    return flatData
  }

  // Return true if this transform should be included.
  var includeTransform = function (variable, runByDefault) {
    if (typeof variable === 'undefined') {
      return runByDefault
    } else if (variable) {
      return true
    } else {
      return false
    }
  }

  //
  // Remove Duplicates
  //
  var removeDuplicates = function (featureCollection) {
    if (isNil(featureCollection) || isNil(featureCollection.features)) {
      return
    }

    if (featureCollection.features.length > 100) {
      // console.log('Consider making this explicit... I.e. must add removeDuplicates flag.')
      return
    }

    if (featureCollection.features) {
      featureCollection.features = uniqWith(featureCollection.features, isEqual)
    }
    return featureCollection
  }

  //
  // Return only nearest five features.
  //
  var nearestFive = function (featureCollection, coords) {
    if (!featureCollection.features) {
      return featureCollection
    }

    let from4326 = coords
    if (isNil(from4326)) {
      from4326 = app.transformTo4326(app.selectMarker.getPosition())
    }
    const fromFeature = point(from4326)

    for (const featureKey in featureCollection.features) {
      const feature = featureCollection.features[featureKey]
      const centroidPt = centroid(feature)
      const toFeature = point(centroidPt.geometry.coordinates)
      feature.properties.distance_from_current_location = distance(fromFeature, toFeature)
    }

    featureCollection.features.sort(function (a, b) {
      return a.properties.distance_from_current_location < b.properties.distance_from_current_location ? -1 : 1
    })

    // Return top 5.
    featureCollection.features = featureCollection.features.slice(0, 5)
    for (const featureTop5Key in featureCollection.features) {
      if (!Object.hasOwnProperty.call(featureCollection.features, featureTop5Key)) continue
      featureCollection.features[featureTop5Key].properties.distance_from_current_location =
        featureCollection.features[featureTop5Key].properties.distance_from_current_location.toFixed(1) + ' km'
    }

    return featureCollection
  }

  //
  // Return only nearest five features.
  //
  var pointOnSurface = function (featureCollection, coords) {
    if (!coords) {
      return featureCollection
    }
    if (!featureCollection) {
      return featureCollection
    }
    if (!featureCollection.features) {
      return featureCollection
    }
    let from4326 = coords
    if (isNil(from4326)) {
      from4326 = app.transformTo4326(app.selectMarker.getPosition())
    }
    const fromFeature = point(from4326)
    for (const featureKey in featureCollection.features) {
      const feature = featureCollection.features[featureKey]
      feature.bPointOnSurface = booleanPointInPolygon(fromFeature, feature)
    }

    remove(featureCollection.features, function (item) {
      return !item.bPointOnSurface
    })

    return featureCollection
  }

  //
  // sortByClosestToSelectionMarker
  //
  var sortByClosestToSelectionMarker = function (featureCollection, coords) {
    if (!featureCollection.features) {
      return featureCollection
    }

    let from4326 = coords
    if (isNil(from4326)) {
      from4326 = app.transformTo4326(app.selectMarker.getPosition())
    }
    const fromFeature = point(from4326)

    for (const featureKey in featureCollection.features) {
      const feature = featureCollection.features[featureKey]
      const centroidPt = centroid(feature)
      const toFeature = point(centroidPt.geometry.coordinates)
      feature.properties.distance_from_current_location = distance(fromFeature, toFeature)
    }

    featureCollection.features.sort(function (a, b) {
      return a.properties.distance_from_current_location < b.properties.distance_from_current_location ? -1 : 1
    })

    return featureCollection
  }

  //
  // Filter
  //
  // Config example:
  //
  // "filter": [
  //   {
  //     "key": "PROPV_GRAPHIC_TYPE",
  //     "value": "P"
  //   },
  //   {
  //     "key": "PROP_STATUS",
  //     "value": "A"
  //   }
  // ]
  //
  const filter = function (config, featureCollection) {
    const filterList = get(config, 'transformer.filter', [])
    const features = get(featureCollection, 'features', [])

    remove(features, (feature) => {
      const props = feature.properties
      let bFound = false
      for (const filterItem of filterList) {
        // Do case-insensitive lookup of propname
        const propName = PoziUtils.findPropName(props, filterItem.key)
        if (propName) {
          const value = props[propName]
          if (filterItem.value === value) {
            bFound = true
          }
        }
      }

      return !bFound
    })

    return featureCollection
  }

  //
  // Set a mask on an exisitng polygon feature
  // Must be a single polygon or multipolgon.
  //
  var geomask = function (config, featureCollection) {
    if (
      !featureCollection ||
      !featureCollection.features ||
      !featureCollection.features[0] ||
      !featureCollection.features[0].geometry ||
      !featureCollection.features[0].geometry.coordinates ||
      !featureCollection.features[0].geometry.coordinates[0]
    ) {
      console.log('Unable to apply geomask to this feature', config)
      return featureCollection
    }

    const polygonMask = [[-180, -90], [-180, 90], [180, 90], [180, -90], [-180, -90]]
    if (featureCollection.features[0].geometry.type === 'Polygon') {
      featureCollection.features[0].geometry.coordinates.unshift(polygonMask)
    } else if (featureCollection.features[0].geometry.type === 'MultiPolygon') {
      featureCollection.features[0].geometry.coordinates[0].unshift(polygonMask)
    } else {
      console.log('Unable to identify the geometry type', config)
    }

    return featureCollection
  }

  //
  // Add an envelope and a mask on the geo data.
  //
  var geomaskEnvelope = function (config, featureCollection) {
    const layerConf = {}
    layerConf.config = config
    const id = PoziUtils.findKeyInFeatureCollection(layerConf, featureCollection)
    if (!id) {
      console.error('Unable to find an id for layer', layerConf)
      return featureCollection
    }

    const featureEnvelope = envelope(featureCollection)
    featureEnvelope.properties = {}
    featureEnvelope.properties[id] = 'featureenvelope'
    const featureMask = polygon([[[-180, -90], [-180, 90], [180, 90], [180, -90], [-180, -90]]])
    featureMask.properties = {}
    featureMask.properties[id] = 'featuremask'
    featureCollection.features.unshift(featureEnvelope)
    featureCollection.features.unshift(featureMask)
    return featureCollection
  }

  var prependIDFieldNameToTitle = function (cardProcessing, flatData) {
    if (cardProcessing.titleVariable && flatData[cardProcessing.titleVariable]) {
      flatData[cardProcessing.titleVariable] = PoziUtils.toTitleCase(cardProcessing.titleVariable + ' ' + flatData[cardProcessing.titleVariable])
    }
    return flatData
  }

  // Does Order Fields...
  var alterfield = function (flatData, transformData) {
    for (const tdKey in transformData) {
      const tdObj = transformData[tdKey] || ''
      const fieldname = get(tdObj, 'fieldname', '')
      const prepend = get(tdObj, 'prepend', '')
      const postpend = get(tdObj, 'postpend', '')
      const replaceArr = get(tdObj, 'findreplace', [])
      if (isNil(tdObj) || fieldname === '') {
        continue
      }
      for (const fdName in flatData) {
        if (fdName.toLowerCase() === fieldname.toLowerCase()) {
          for (const replaceKey in replaceArr) {
            const replaceObj = replaceArr[replaceKey]
            const findStr = replaceObj.find
            const replaceStr = replaceObj.replace
            flatData[fdName] = flatData[fdName].replace(findStr, replaceStr)
          }
          flatData[fdName] = prepend + flatData[fdName] + postpend
        }
      }
    }
    return flatData
  }

  var postpendToTitle = function (cardProcessing, flatData, transformData) {
    if (cardProcessing.titleVariable && flatData[cardProcessing.titleVariable]) {
      flatData[cardProcessing.titleVariable] = PoziUtils.toTitleCase(flatData[cardProcessing.titleVariable]) + ' ' + transformData
    }
    return flatData
  }

  var vmCLTenureLookup = function (flatData) {
    if (flatData.CLTEN_TENURE_CODE && globalTransformerCrownLandTenureCode[flatData.CLTEN_TENURE_CODE]) {
      flatData.Description = globalTransformerCrownLandTenureCode[flatData.CLTEN_TENURE_CODE]
    }
    if (flatData.CLTEN_TENURE_TYPE && globalTransformerCrownLandTenureType[flatData.CLTEN_TENURE_TYPE]) {
      flatData.Type = globalTransformerCrownLandTenureType[flatData.CLTEN_TENURE_TYPE]
    }
    return flatData
  }

  var soilTypeLookup = function (flatData) {
    if (flatData.asc_o && globalTransformerSoilType[flatData.asc_o]) {
      flatData.order = globalTransformerSoilType[flatData.asc_o]
    }
    if (flatData.asc_so && globalTransformerSoilType[flatData.asc_so]) {
      flatData.sub_order = globalTransformerSoilType[flatData.asc_so]
    }
    return flatData
  }

  var removeZFromEndOfDates = function (flatData) {
    for (const key in flatData) {
      if (!Object.hasOwnProperty.call(flatData, key)) continue
      if (
        flatData[key] &&
        typeof flatData[key] === 'string' &&
        (key.toLowerCase().indexOf('date') > -1 || key.toLowerCase().indexOf('_start') > -1) &&
        endsWith(flatData[key], 'Z')
      ) {
        const d = new Date(flatData[key].substring(0, flatData[key].length - 1))
        if (!d || d.toString() === 'Invalid Date') {
          console.log('Looked like a date, but unable to parse so returning original data.')
          return flatData
        }
        flatData[key] = flatData[key].substring(0, flatData[key].length - 1)
      }
    }
    return flatData
  }

  var formatDates = function (flatData, includetime) {
    console.log('formatDates')
    for (const key in flatData) {
      if (!Object.hasOwnProperty.call(flatData, key)) continue
      if (flatData[key] && typeof flatData[key] === 'string' && key.toLowerCase().indexOf('date') > -1) {
        flatData[key] = getDateAsString(flatData[key], includetime)
      }
    }
    return flatData
  }

  // Currently disabled
  const removeEmptyFields = function (flatData) {
    // console.log("removeEmptyFields");
    for (const key in flatData) {
      if (!Object.hasOwnProperty.call(flatData, key)) continue
      if (!flatData[key] && !(typeof flatData[key] === 'number')) {
        delete flatData[key]
      } else if (typeof flatData[key] === 'string') {
        if (trim(flatData[key]) === '') {
          // console.log(key + " -> " + flatData[key]);
          delete flatData[key]
        }
      }
    }
    return flatData
  }

  var hidePastEvents = function (flatData, config) {
    console.log('hidePastEvents NYI')
    /*
    if (!config.key) {
      console.log("hidePastEvents could not find a valid key");
      return flatData;
    }

    var dateString = ""
    var theDate

    for (var key in flatData) {
      if (Object.hasOwnProperty.call(flatData, key)) {
        if (!flatData[config.key]) {
          console.log("hidePastEvents could not find a key in the data");
          return flatData;
        }
        dateString = flatData[config.key]
        theDate = Date.parse(dateString)
        if (theDate) {
          console.log("hidePastEvents found date: " + theDate.toString());
        } else {
          console.log("hidePastEvents unable to parse date string: " + flatData[config.key]);
        }

        // TODO: parse dates and remove if in the past.
        // delete flatData[key];
      }
    } */
    return flatData
  }

  var roundValue = function (flatData, transformData) {
    for (const key in transformData) {
      if (!Object.hasOwnProperty.call(transformData, key)) continue
      if (transformData[key] && flatData[key]) {
        flatData[key] = 'Approx. ' + Math.round(flatData[key])
      }
    }
    return flatData
  }

  // This function receives an array of prop names. We will try to find the fields
  // in the data and reorder that data accordingly
  const orderFields = (flatData, transformData) => {
    // console.debug('orderFields', Object.keys(flatData), transformData)

    const flatDataOrdered = {}

    for (const tdPropName of transformData) {
      // Do a case insensitive lookup for the prop name in the data and use that
      const fdPropName = PoziUtils.findPropName(flatData, tdPropName)
      if (fdPropName) {
        // console.debug('orderFields - found', tdPropName, fdPropName)

        flatDataOrdered[fdPropName] = flatData[fdPropName]
        delete flatData[fdPropName]
      }
    }

    // Add the remaining fields in the original order
    for (const fdPropName in flatData) {
      flatDataOrdered[fdPropName] = flatData[fdPropName]
    }

    // console.debug('orderFields - flatDataOrdered', Object.keys(flatDataOrdered))

    return flatDataOrdered
  }

  // Does Order Fields...
  // See http://stackoverflow.com/questions/5525795/does-javascript-guarantee-object-property-order
  const renameAndOrderFields = (flatData, transformData) => {
    // console.debug('renameAndOrderFields', { ...flatData }, { ...transformData })

    const flatData2 = {}
    // Add defined fields first, in order.

    for (const tdIndex in transformData) {
      const tdObj = transformData[tdIndex] || {} // e.g. { "rub_day": "Rubbish Bin" }
      const tdPropName = Object.keys(tdObj)[0] || '' // get name of first property of object (we assume there is only one)

      // console.debug('renameAndOrderFields - tdIndex, tdObj, tdPropName', tdIndex, tdPropName, tdObj)

      if (isNil(tdObj) || tdPropName === '') {
        continue
      }

      // Try to find the case sensitive version of the flat data property name that
      // matches the transform data property name
      // e.g. "rub_day" => "Rub_day"
      const fdPropName = PoziUtils.findPropName(flatData, tdPropName)

      if (fdPropName) { // e.g. "Rub_day"
        // e.g. flatData2["Rubbish Bin"] = flatData["Rub_name"]
        const fdNewKey = tdObj[tdPropName] // e.g. "rub_day" => "Rubbish Bin"
        flatData2[fdNewKey] = flatData[fdPropName]

        delete flatData[fdPropName]

        // console.debug('renameAndOrderFields - tdPropName, fdPropName, fdNewKey', fdNewKey, flatData2)
      } else {
        console.log('renameAndOrderFields - field cannot be found in the data:', tdPropName, { ...flatData }, { ...transformData })
      }
    }

    // Add the remaining fields in the original order
    for (const fdName2 in flatData) {
      flatData2[fdName2] = flatData[fdName2]
    }

    // console.log('renameAndOrderFields - end result:', Object.keys(flatData2))

    return flatData2
  }

  var removeFields = function (flatData, transformData) {
    for (const key in transformData) {
      if (!Object.hasOwnProperty.call(transformData, key)) continue
      if (transformData[key] && flatData[key]) {
        delete flatData[key]
      }
    }
    return flatData
  }

  var removeFieldsArr = function (flatData, transformData) {
    for (let i = 0; i < transformData.length; i++) {
      const find = toLower(transformData[i])
      const excludeArr = Object.keys(flatData)
      for (let j = 0; j < excludeArr.length; j++) {
        const match = toLower(excludeArr[j])
        if (find === match) {
          delete flatData[excludeArr[j]]
          break
        }
      }
    }
    return flatData
  }

  /**
   * To chop off 5 characters from start use:
   *    { field: "abc", start: 5, end: "" }
   *
   * To chop off 5 characters from end use:
   *    { field: "abc", start: 0, end: -5 }
   *
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/slice
   *
   * @param {geojson feature properties} flatData
   * @param {object} transformData: {field, start, end}
   *
   */
  var sliceFields = function (flatData, transformData) {
    for (const key in transformData) {
      const field = transformData[key].featureProperty
      const start = transformData[key].start || 0
      const end = transformData[key].end || ''
      if (!field || !flatData || !flatData[field]) {
        continue
      }
      if (end === '') {
        flatData[field] = flatData[field].slice(start) // chop from some start position to end of string.
      } else {
        flatData[field] = flatData[field].slice(start, end)
      }
    }

    return flatData
  }

  const calculatedField = (flatData, transformData) => {
    // Convert transformData to array if not already
    if (!(transformData instanceof Array)) {
      transformData = [transformData]
    }

    for (const transformDataElement of transformData) {
      // Do case-insensitive lookup of field name in flatData
      const fieldName = PoziUtils.findPropName(flatData, transformDataElement.field)
      if (fieldName) {
        flatData[transformDataElement.title] = transformDataElement.value.replace('{field}', flatData[fieldName])
      }
    }

    return flatData
  }

  /*
  const transformData = [{
    featureProperty: 'RMACLASS',
    featureValue: 'AO',
    styles: [{
      layerProperty: 'stroke-color', // E.g. strokeHex (See geojsonio for list of fields)
      layerValue: '#555555'
    }, {
      layerProperty: 'stroke-width', // E.g. strokeHex (See geojsonio for list of fields)
      layerValue: 3
    }]
  },
  {
    featureProperty: 'RMACLASS',
    featureValue: 'AH',
    styles: [{
      layerProperty: 'stroke-color', // E.g. strokeHex (See geojsonio for list of fields)
      layerValue: '#F5301A'
    }, {
      layerProperty: 'stroke-width', // E.g. strokeHex (See geojsonio for list of fields)
      layerValue: 7
    }]
  }] */

  var addFeatureFields = function (flatData, transformData) {
    if (!isArray(transformData)) {
      console.log('Error: The addFeatureFields transformer must be an array of style elements.')
      return flatData
    }
    for (const key in transformData) {
      const transformDataElement = transformData[key]
      if (flatData[transformDataElement.featureProperty] && flatData[transformDataElement.featureProperty] === transformDataElement.featureValue) {
        if (!isArray(transformDataElement.newFeatureProperties)) {
          console.log('Error: The addFeatureFields newFeatureProperties must be an array.')
          continue
        }
        const newFeaturePropertiesArr = transformDataElement.newFeatureProperties
        for (const newFeaturePropertiesKey in newFeaturePropertiesArr) {
          const newFeatureProperties = newFeaturePropertiesArr[newFeaturePropertiesKey]
          const newFeatureProperty = get(newFeatureProperties, 'newFeatureProperty', '')
          const newFeatureValue = get(newFeatureProperties, 'newFeatureValue', '')
          if (newFeatureProperty === '' || newFeatureValue === '') {
            console.log('Error: The addFeatureFields layer properties and values must not be blank.')
            continue
          }
          flatData[newFeatureProperty] = newFeatureValue
        }
      }
    }
    return flatData
  }

  // Day, Day of Month, Month, year
  var getDateAsString = function (date, includetime) {
    let d
    if (typeof date === 'object') {
      // console.log("Date is already an object.");
      d = date
    } else {
      // console.log("Date is not an object need to parse: " + date);
      d = new Date(date)
    }

    if (!d || d.toString() === 'Invalid Date') {
      console.log('Unable to parse date. returning original data.')
      return date
    }

    const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

    const n = weekdayNames[d.getDay()]
    const mmm = monthNamesShort[d.getMonth()]

    let timeWithCommaIfNotZero = ''

    if (includetime) {
      if (d.getHours() > 0 || d.getSeconds() > 0) {
        const hours = d.getHours() > 9 ? d.getHours() : '0' + d.getHours()
        const seconds = d.getSeconds() > 9 ? d.getSeconds() : '0' + d.getSeconds()

        timeWithCommaIfNotZero = ', ' + hours + ':' + seconds
      }
    }

    return n + ' ' + d.getDate() + ' ' + mmm + ' ' + d.getFullYear() + timeWithCommaIfNotZero
  }

  // Return adapters (must be at end of adapter)
  return adapters
}
export default PoziDataTransformer
// End A (Adapter)
